'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Button, StarRating } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_GRAY,
  BUTTON_VARIANT_OUTLINED,
} from '@sgcmpteltd/uikit-react/components/button';
import { isRatingValue } from '@sgcmpteltd/uikit-react/components/star-rating';
import { clsx as cx } from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import { Tooltip } from '@/components/tooltip';
import { getFormattedDateString } from '@/utils/string/formattedDate';
import { joinSgcmMedia } from '@/utils/url/join/public';
import { joinMyCarForumSite } from '@/utils/url/join/public/util';

import {
  CardWithAvatar,
} from '../../../_components/page-content/card-with-avatar';
import {
  COMFORT_TEXT,
  EXTERIOR_TEXT,
  FEATURES_TEXT,
  HANDLING_TEXT,
  INTERIOR_TEXT,
  PERFORMANCE_TEXT,
  RELIABILITY_TEXT,
  VALUE_TEXT,
} from '../../../submit-review/_components/page-content/constants';
import {
  ICON_SIZE_IN_PIXEL,
  STAR_ICON_SIZE_IN_PIXEL,
  TEXTBOX_ICON_SIZE_IN_PIXEL,
} from '../../constants';
import { rankMap } from './config';
import {
  GRADE_TO_POINT_LINK,
  MOBILE_READ_MORE,
  MOBILE_SHOW_LESS,
  POINT_DESCRIPTION,
  RANKING_DESCRIPTION,
  REVIEWER_LINK,
} from './constant';
import styles from './styles.module.scss';
import type { Component } from './types';

export const ConsumerReview: Component = ({
  consumerReview,
}) => {
  const [expandReviews, setExpandReviews] = useState<number | null>(null);
  const { isIpadMini } = useScreenDetect();
  const isIpadMiniScreen = isIpadMini();
  const showReadMoreButton = consumerReview.like
    || consumerReview.dislike
    || consumerReview.detailedRatings;
  const isExpandConsumerReview = expandReviews
    === consumerReview.id;
  const formattedOverallRate = (
    rate?: number | null,
  ) => (rate ? rate.toFixed(1) : '0.0');

  const findMemberRank = (points: number) => rankMap.slice().reverse().find(
    rank => points >= rank.points,
  );

  const formatReviewText = (text: string) => text.split('\n').map(line => (
    <p key={line}>
      {line}
      <br />
    </p>
  ));

  const getMemberRank = (points?: number | null): string => {
    if (points === undefined || points === null) {
      return 'Unknown';
    }
    const memberRank = findMemberRank(points);
    return memberRank ? memberRank.rank : 'Unknown';
  };

  const handleReadMoreButtonClick = (id: number) => () => {
    if (id === expandReviews) {
      setExpandReviews(null);
      return;
    }
    setExpandReviews(id);
  };
  return (
    <div
      key={consumerReview.id}
      className={styles.containerConsumerReview}
      id={`comments${consumerReview.id}`}>
      <CardWithAvatar
        avatar={consumerReview.member?.image ?? 'https://www.mycarforum.com/public/style_images/master/profile/default_large.png'}
        lowerChildNode={(
          isIpadMiniScreen && (
            <>
              <div className={
                styles.containerAvatarBottomItem
              }>
                <Image
                  alt="icon image"
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/textbox.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
                <Link
                  className={styles.reviewerLink}
                  href={`${REVIEWER_LINK}&id=${consumerReview.member?.id}`}
                  target="_blank">
                  {consumerReview.member?.postCount}
                  {' '}
                  reviews
                </Link>
              </div>
              <div className={
                styles.containerAvatarBottomItem
              }>
                <Image
                  alt="icon image"
                  height={ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/stereoscopicCoin.svg')}`}
                  width={ICON_SIZE_IN_PIXEL} />
                <span>
                  {consumerReview.member?.memberPoints}
                  {' '}
                  Points
                </span>
                <Tooltip
                  className={cx(
                    styles.containerTooltip,
                    styles.containerGradeTooltip,
                  )}
                  content={(
                    <div className={cx(
                      styles.containerTooltipContent,
                    )}>
                      <div className={
                        styles.containerRankToolTip
                      }>
                        {POINT_DESCRIPTION}
                      </div>
                    </div>
                  )}>
                  <button
                    className={
                      styles.buttonRecommendedPriceDesc
                    }>
                    <Image
                      alt="info icon image"
                      className={cx(
                        styles.imageHoverIcon,
                      )}
                      height={ICON_SIZE_IN_PIXEL}
                      src={`${joinSgcmMedia('/public/new-cars/icon/iconOutlineInfo.svg')}`}
                      width={ICON_SIZE_IN_PIXEL} />
                  </button>
                </Tooltip>
              </div>
            </>
          )
        )}
        showDivider={isIpadMiniScreen}
        upperChildNode={
          isIpadMiniScreen
            ? (
              <div className={styles.containerContent}>
                <div className={styles.containerName}>
                  <Link
                    className={styles.contentName}
                    href={joinMyCarForumSite(`/profile/${consumerReview.member?.id}-${consumerReview.member?.name}/`)}>
                    {consumerReview.member?.name}
                  </Link>
                </div>
                <div className={styles.containerGrade}>
                  <span className={styles.gradeRank}>
                    {getMemberRank(
                      consumerReview.member?.memberPoints,
                    )}
                  </span>
                  <Tooltip
                    className={cx(
                      styles.containerTooltip,
                      styles.containerGradeTooltip,
                    )}
                    content={(
                      <div className={cx(
                        styles.containerTooltipContent,
                      )}>
                        <div className={
                          styles.containerRankToolTip
                        }>
                          <div className={
                            styles.textTooltipTitle
                          }>
                            {RANKING_DESCRIPTION}
                          </div>
                          <table className={
                            styles.containerRankTable
                          }>
                            <colgroup>
                              <col style={{ width: '50%' }} />
                              <col style={{ width: '50%' }} />
                            </colgroup>
                            <thead>
                              <tr className={
                                styles.containerRankTableHead
                              }>
                                <th className={
                                  styles.contentRankTable
                                }>
                                  Ranks
                                </th>
                                <th className={
                                  styles.contentRankTable
                                }>
                                  Points
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {rankMap.map((
                                { rank, points },
                              ) => (
                                <tr
                                  key={rank}
                                  className={
                                    styles
                                      .containerRankTableBody
                                  }>
                                  <td className={
                                    styles.contentRankTable
                                  }>
                                    {rank}
                                  </td>
                                  <td className={
                                    styles.contentRankTable
                                  }>
                                    {'> '}
                                    {points}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div>
                            More about the
                            ranking system click
                            {' '}
                            <Link
                              className={
                                styles.rankToolTipHereLink
                              }
                              href={GRADE_TO_POINT_LINK}
                              target="_blank">
                              here
                            </Link>
                            <span>
                              .
                            </span>
                          </div>
                        </div>
                      </div>
                    )}>
                    <button
                      className={
                        styles.buttonRecommendedPriceDesc
                      }>
                      <Image
                        alt="info icon image"
                        className={cx(
                          styles.imageHoverIcon,
                        )}
                        height={ICON_SIZE_IN_PIXEL}
                        src={`${joinSgcmMedia('/public/new-cars/icon/iconOutlineInfo.svg')}`}
                        width={ICON_SIZE_IN_PIXEL} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            )
            : consumerReview.member && (
              <div className={styles.containerContent}>
                <div className={styles.containerName}>
                  <span className={styles.contentName}>
                    {consumerReview.member.name}
                  </span>
                  {' | '}
                  {getFormattedDateString(
                    consumerReview.member.joinedAt,
                    'shorten',
                  )}
                </div>
                <StarRating
                  iconSize={STAR_ICON_SIZE_IN_PIXEL}
                  value={isRatingValue(consumerReview.rating)
                    ? consumerReview.rating
                    : 0} />
              </div>
            // eslint-disable-next-line @stylistic/jsx-indent
            )
        } />
      <div className={styles.containerConsumerReviewBlock}>
        <div className={styles.containerConsumerReviewHead}>
          <div className={styles.consumerReviewTitle}>
            {consumerReview.title}
          </div>
          {
            isIpadMiniScreen && (
              <div className={styles.consumerReviewPostOn}>
                Posted on:
                {' '}
                {getFormattedDateString(
                  consumerReview.postedAt,
                  'shorten',
                  '-',
                )}
              </div>
            )
          }
        </div>
        {isIpadMiniScreen && (
          <div className={
            styles.containerConsumerReviewScore
          }>
            {
              consumerReview.rating && (
                <div className={
                  styles.conatinerConsumerOverallReviewScore
                }>
                  <div className={styles.consumerReviewScore}>
                    {formattedOverallRate(consumerReview.rating)}
                  </div>
                  <StarRating
                    iconSize={STAR_ICON_SIZE_IN_PIXEL}
                    value={isRatingValue(consumerReview.rating)
                      ? consumerReview.rating
                      : 0} />
                </div>
              )
            }
            {
              consumerReview.detailedRatings && (
                <div className={styles.containerRatingBlocks}>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview.detailedRatings.comfort,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {COMFORT_TEXT}
                    </div>
                  </div>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview
                          .detailedRatings.reliability,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {RELIABILITY_TEXT}
                    </div>
                  </div>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview.detailedRatings.interior,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {INTERIOR_TEXT}
                    </div>
                  </div>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview
                          .detailedRatings.valueForMoney,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {VALUE_TEXT}
                    </div>
                  </div>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview.detailedRatings.handling,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {HANDLING_TEXT}
                    </div>
                  </div>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview.detailedRatings.features,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {FEATURES_TEXT}
                    </div>
                  </div>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview
                          .detailedRatings.performance,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {PERFORMANCE_TEXT}
                    </div>
                  </div>
                  <div
                    className={styles.containerRatingBlock}>
                    <div className={styles.consumerRatingScore}>
                      {formattedOverallRate(
                        consumerReview.detailedRatings.exterior,
                      )}
                    </div>
                    <div className={styles.consumerRatingText}>
                      {EXTERIOR_TEXT}
                    </div>
                  </div>
                </div>
              )
            }
          </div>
        )}
        <div className={
          styles.containerConsumerReviewContent
        }>
          <Image
            alt="icon image"
            className={styles.imageThumbnail}
            height={18}
            src={`${joinSgcmMedia('/public/new-cars/icon/textbox.svg')}`}
            width={18} />
          <div className={styles.sectionText}>
            Review
          </div>
        </div>
        <div className={styles.contentConsumerReview}>
          {formatReviewText(consumerReview.review)}
        </div>
        {
          (consumerReview.like
          && (isExpandConsumerReview || isIpadMiniScreen))
          && (
            <>
              <div className={
                styles.containerConsumerReviewContent
              }>
                <Image
                  alt="icon image"
                  className={styles.imageThumbnail}
                  height={16}
                  src={`${joinSgcmMedia('/public/new-cars/icon/smile.svg')}`}
                  width={16} />
                <div className={styles.sectionText}>
                  What I Like
                </div>
              </div>
              <div className={styles.contentConsumerReview}>
                {formatReviewText(consumerReview.like)}
              </div>
            </>
          )
        }
        {
          (consumerReview.dislike
          && (isExpandConsumerReview || isIpadMiniScreen))
          && (
            <>
              <div className={
                styles.containerConsumerReviewContent
              }>
                <Image
                  alt="icon image"
                  className={styles.imageThumbnail}
                  height={TEXTBOX_ICON_SIZE_IN_PIXEL}
                  src={`${joinSgcmMedia('/public/new-cars/icon/sad.svg')}`}
                  width={TEXTBOX_ICON_SIZE_IN_PIXEL} />
                <div className={styles.sectionText}>
                  What I Dislike
                </div>
              </div>
              <div className={styles.contentConsumerReview}>
                {formatReviewText(consumerReview.dislike)}
              </div>
            </>
          )
        }
        {
          !isIpadMiniScreen && isExpandConsumerReview
          && consumerReview.detailedRatings && (
            <>
              <div className={
                styles.containerConsumerReviewContent
              }>
                <div className={styles.sectionText}>
                  Overall Rating
                </div>
              </div>
              <div className={
                styles.containerConsumerReviewEachRating
              }>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview.detailedRatings.comfort,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {COMFORT_TEXT}
                  </div>
                </div>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview.detailedRatings.interior,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {INTERIOR_TEXT}
                  </div>
                </div>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview.detailedRatings.handling,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {HANDLING_TEXT}
                  </div>
                </div>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview
                        .detailedRatings.performance,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {PERFORMANCE_TEXT}
                  </div>
                </div>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview
                        .detailedRatings.reliability,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {RELIABILITY_TEXT}
                  </div>
                </div>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview
                        .detailedRatings.valueForMoney,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {VALUE_TEXT}
                  </div>
                </div>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview.detailedRatings.features,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {FEATURES_TEXT}
                  </div>
                </div>
                <div
                  className={styles.containerRatingBlock}>
                  <div className={styles.consumerRatingScore}>
                    {formattedOverallRate(
                      consumerReview.detailedRatings.exterior,
                    )}
                  </div>
                  <div className={styles.consumerRatingText}>
                    {EXTERIOR_TEXT}
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          !isIpadMiniScreen && isExpandConsumerReview && (
            <div className={styles.consumerReviewPostOn}>
              Posted on:
              {' '}
              {getFormattedDateString(
                consumerReview.postedAt,
                'shorten',
                '-',
              )}
            </div>
          )
        }
      </div>
      {
        !isIpadMiniScreen && showReadMoreButton && (
          <Button
            className={styles.readMoreButton}
            color={BUTTON_COLOR_GRAY}
            variant={BUTTON_VARIANT_OUTLINED}
            onClick={
              handleReadMoreButtonClick(consumerReview.id)
            }>
            <span className={styles.submitText}>
              {isExpandConsumerReview
                ? MOBILE_SHOW_LESS
                : MOBILE_READ_MORE}
            </span>
          </Button>
        )
      }
    </div>
  );
};
